import React, { useState } from 'react';
import { TestTube, Database, Lock } from 'lucide-react';
import { useDataMode, type DataMode } from '@/contexts/DataModeContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * DataModeSelector Component
 * 
 * A sleek toggle switch for switching between Demo and Live data modes.
 * 
 * Features:
 * - Animated sliding indicator
 * - Icons for visual clarity (TestTube for Demo, Database for Live)
 * - Confirmation dialog when switching to Live mode
 * - Tooltip support for additional context
 * - Full keyboard accessibility
 * - EDS-compliant styling with dark mode support
 */

export function DataModeSelector() {
  const { mode, setMode, isModeLocked } = useDataMode();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMode, setPendingMode] = useState<DataMode | null>(null);

  const handleModeClick = (newMode: DataMode) => {
    if (newMode === mode) return; // Already in this mode

    if (newMode === 'live') {
      // Show confirmation dialog for Live mode
      setPendingMode(newMode);
      setShowConfirmDialog(true);
    } else {
      // Switch to Demo mode immediately
      setMode(newMode);
    }
  };

  const handleConfirmLiveMode = () => {
    if (pendingMode) {
      setMode(pendingMode);
    }
    setShowConfirmDialog(false);
    setPendingMode(null);
  };

  const handleCancelLiveMode = () => {
    setShowConfirmDialog(false);
    setPendingMode(null);
  };

  if (isModeLocked) {
    if (mode !== 'demo') {
      return null;
    }

    return (
      <div className="flex items-center gap-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <Lock className="h-3.5 w-3.5" aria-hidden="true" />
              Demo
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Mode is locked in this app variant</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-muted-foreground">Data Mode:</span>
        
        <div
          className="relative inline-flex items-center rounded-lg bg-muted p-1 shadow-sm"
          role="radiogroup"
          aria-label="Data mode selector"
        >
          {/* Sliding indicator background */}
          <div
            className="absolute inset-y-1 w-[calc(50%-0.25rem)] rounded-md bg-primary shadow-md transition-transform duration-200 ease-out"
            style={{
              transform: mode === 'live' ? 'translateX(calc(100% + 0.25rem))' : 'translateX(0)',
            }}
            aria-hidden="true"
          />

          {/* Demo Mode Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => handleModeClick('demo')}
                className={`relative z-10 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  mode === 'demo'
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                role="radio"
                aria-checked={mode === 'demo'}
                aria-label="Demo mode"
              >
                <TestTube className="h-4 w-4" aria-hidden="true" />
                <span>Demo</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Use mock/demo data</p>
            </TooltipContent>
          </Tooltip>

          {/* Live Mode Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => handleModeClick('live')}
                className={`relative z-10 flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  mode === 'live'
                    ? 'text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                role="radio"
                aria-checked={mode === 'live'}
                aria-label="Live mode"
              >
                <Database className="h-4 w-4" aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Use real backend data</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Confirmation Dialog for Live Mode */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch data mode?</DialogTitle>
            <DialogDescription>
              You are about to switch to real data from the backend.
              Make sure your backend is properly configured and running.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelLiveMode}>
              Cancel
            </Button>
            <Button onClick={handleConfirmLiveMode}>
              Switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
